import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  Download, 
  DollarSign, 
  Package, 
  TrendingUp
} from 'lucide-react';

export default function ReportsPage() {
  const [activeReport, setActiveReport] = useState('profit');
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseFilter, setWarehouseFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [valuationData, setValuationData] = useState({ items: [], total_value: 0 });
  const [stockData, setStockData] = useState([]);
  const [profitData, setProfitData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWarehouses();
    // Set default date range to current month
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    setStartDate(firstDay.toISOString().split('T')[0]);
    setEndDate(lastDay.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    fetchReportData();
  }, [activeReport, warehouseFilter, startDate, endDate]);

  const fetchWarehouses = async () => {
    try {
      const response = await api.get('/warehouses');
      setWarehouses(response.data);
    } catch (error) {
      console.error('Failed to fetch warehouses');
    }
  };

  const fetchReportData = async () => {
    setLoading(true);
    try {
      if (activeReport === 'valuation') {
        await fetchValuationReport();
      } else if (activeReport === 'stock') {
        await fetchStockReport();
      } else if (activeReport === 'profit') {
        await fetchProfitReport();
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchValuationReport = async () => {
    try {
      const params = new URLSearchParams();
      if (warehouseFilter && warehouseFilter !== 'all') params.append('warehouse_id', warehouseFilter);
      
      const response = await api.get(`/reports/valuation?${params.toString()}`);
      setValuationData(response.data);
    } catch (error) {
      toast.error('Failed to fetch valuation report');
    }
  };

  const fetchStockReport = async () => {
    try {
      const params = new URLSearchParams();
      if (warehouseFilter && warehouseFilter !== 'all') params.append('warehouse_id', warehouseFilter);
      
      const response = await api.get(`/reports/stock-on-hand?${params.toString()}`);
      setStockData(response.data);
    } catch (error) {
      toast.error('Failed to fetch stock report');
    }
  };

  const fetchProfitReport = async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const response = await api.get(`/reports/profit?${params.toString()}`);
      setProfitData(response.data);
    } catch (error) {
      toast.error('Failed to fetch profit report');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-JM', {
      style: 'currency',
      currency: 'JMD',
      minimumFractionDigits: 2
    }).format(value).replace('JMD', 'J$');
  };

  const handleExportMovements = async () => {
    try {
      const response = await api.get('/export/movements');
      const blob = new Blob([response.data.csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      a.click();
      toast.success('Export complete');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-heading-black text-3xl lg:text-4xl text-[#0A0A0A] tracking-tight">REPORTS</h1>
          <p className="text-[#0A0A0A]/60 mt-1">Financial reports and profit tracking</p>
        </div>
        <Button variant="outline" onClick={handleExportMovements} data-testid="export-movements-btn">
          <Download className="w-4 h-4 mr-2" />
          Export Movements
        </Button>
      </div>

      {/* Report Type Tabs */}
      <Card className="border border-[#E5E5EA] shadow-none">
        <CardContent className="p-4">
          <Tabs value={activeReport} onValueChange={setActiveReport}>
            <TabsList className="grid w-full max-w-xl grid-cols-3">
              <TabsTrigger value="profit" className="flex items-center gap-2" data-testid="profit-report-tab">
                <TrendingUp className="w-4 h-4" />
                Profit & Loss
              </TabsTrigger>
              <TabsTrigger value="valuation" className="flex items-center gap-2" data-testid="valuation-report-tab">
                <DollarSign className="w-4 h-4" />
                Valuation
              </TabsTrigger>
              <TabsTrigger value="stock" className="flex items-center gap-2" data-testid="stock-report-tab">
                <Package className="w-4 h-4" />
                Stock
              </TabsTrigger>
            </TabsList>
          </Tabs>
          
          {/* Filters */}
          <div className="flex flex-wrap gap-4 mt-4">
            {activeReport === 'profit' && (
              <>
                <div className="space-y-1">
                  <Label className="text-xs text-[#0A0A0A]/50">Start Date</Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-40"
                    data-testid="start-date-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-[#0A0A0A]/50">End Date</Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-40"
                    data-testid="end-date-input"
                  />
                </div>
              </>
            )}
            {(activeReport === 'valuation' || activeReport === 'stock') && (
              <div className="space-y-1">
                <Label className="text-xs text-[#0A0A0A]/50">Warehouse</Label>
                <Select value={warehouseFilter} onValueChange={setWarehouseFilter}>
                  <SelectTrigger className="w-48" data-testid="report-warehouse-filter">
                    <SelectValue placeholder="All Warehouses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Warehouses</SelectItem>
                    {warehouses.map((wh) => (
                      <SelectItem key={wh.id} value={wh.id}>{wh.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Profit & Loss Report */}
      {activeReport === 'profit' && profitData && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border border-[#E5E5EA] shadow-none bg-[#007AFF]/5">
              <CardContent className="p-6">
                <p className="text-overline text-[#0A0A0A]/50 mb-2">Gross Revenue</p>
                <p className="kpi-number text-2xl text-[#007AFF]">{formatCurrency(profitData.summary?.gross_revenue || 0)}</p>
              </CardContent>
            </Card>
            <Card className="border border-[#E5E5EA] shadow-none">
              <CardContent className="p-6">
                <p className="text-overline text-[#0A0A0A]/50 mb-2">Cost of Goods</p>
                <p className="kpi-number text-2xl text-[#0A0A0A]">{formatCurrency(profitData.summary?.cost_of_goods_sold || 0)}</p>
              </CardContent>
            </Card>
            <Card className="border border-[#E5E5EA] shadow-none bg-[#34C759]/5">
              <CardContent className="p-6">
                <p className="text-overline text-[#0A0A0A]/50 mb-2">Gross Profit</p>
                <p className="kpi-number text-2xl text-[#34C759]">
                  {formatCurrency(profitData.summary?.gross_profit || 0)}
                  <span className="text-sm ml-2">({profitData.summary?.profit_margin || 0}%)</span>
                </p>
              </CardContent>
            </Card>
            <Card className="border border-[#E5E5EA] shadow-none">
              <CardContent className="p-6">
                <p className="text-overline text-[#0A0A0A]/50 mb-2">Units Sold</p>
                <p className="kpi-number text-2xl text-[#0A0A0A]">{profitData.summary?.units_sold || 0}</p>
              </CardContent>
            </Card>
          </div>

          {/* Profit by Item */}
          <Card className="border border-[#E5E5EA] shadow-none">
            <CardHeader className="border-b border-[#E5E5EA] pb-4">
              <CardTitle className="text-heading text-lg">Profit by Item</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#007AFF]"></div>
                </div>
              ) : !profitData.items?.length ? (
                <div className="flex flex-col items-center justify-center h-64 text-center">
                  <TrendingUp className="w-12 h-12 text-[#0A0A0A]/20 mb-4" />
                  <p className="text-[#0A0A0A]/60">No sales data for this period</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-b border-[#E5E5EA]">
                      <TableHead className="text-overline text-[#0A0A0A]/50">SKU</TableHead>
                      <TableHead className="text-overline text-[#0A0A0A]/50">Item</TableHead>
                      <TableHead className="text-overline text-[#0A0A0A]/50 text-right">Qty Sold</TableHead>
                      <TableHead className="text-overline text-[#0A0A0A]/50 text-right">Revenue</TableHead>
                      <TableHead className="text-overline text-[#0A0A0A]/50 text-right">Cost</TableHead>
                      <TableHead className="text-overline text-[#0A0A0A]/50 text-right">Profit</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {profitData.items.map((item, index) => (
                      <TableRow key={index} className="border-b border-[#E5E5EA]">
                        <TableCell className="font-mono text-sm">{item.sku}</TableCell>
                        <TableCell className="font-medium">{item.name}</TableCell>
                        <TableCell className="text-right font-mono">{item.quantity_sold}</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(item.revenue)}</TableCell>
                        <TableCell className="text-right font-mono text-[#0A0A0A]/60">{formatCurrency(item.cost)}</TableCell>
                        <TableCell className={`text-right font-mono font-medium ${item.profit >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'}`}>
                          {formatCurrency(item.profit)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Valuation Report */}
      {activeReport === 'valuation' && (
        <>
          {/* Summary */}
          <Card className="border border-[#E5E5EA] shadow-none bg-[#007AFF]/5">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-overline text-[#0A0A0A]/50 mb-2">TOTAL INVENTORY VALUE</p>
                  <p className="kpi-number text-4xl text-[#0A0A0A]">{formatCurrency(valuationData.total_value)}</p>
                </div>
                <div className="w-16 h-16 bg-[#007AFF]/10 rounded-sm flex items-center justify-center">
                  <DollarSign className="w-8 h-8 text-[#007AFF]" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Valuation Table */}
          <Card className="border border-[#E5E5EA] shadow-none">
            <CardHeader className="border-b border-[#E5E5EA] pb-4">
              <CardTitle className="text-heading text-lg">Valuation Details</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#007AFF]"></div>
                </div>
              ) : valuationData.items.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-center">
                  <DollarSign className="w-12 h-12 text-[#0A0A0A]/20 mb-4" />
                  <p className="text-[#0A0A0A]/60">No valuation data available</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-b border-[#E5E5EA]">
                      <TableHead className="text-overline text-[#0A0A0A]/50">SKU</TableHead>
                      <TableHead className="text-overline text-[#0A0A0A]/50">Item Name</TableHead>
                      <TableHead className="text-overline text-[#0A0A0A]/50">Warehouse</TableHead>
                      <TableHead className="text-overline text-[#0A0A0A]/50 text-right">Quantity</TableHead>
                      <TableHead className="text-overline text-[#0A0A0A]/50 text-right">Avg Cost</TableHead>
                      <TableHead className="text-overline text-[#0A0A0A]/50 text-right">Total Value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {valuationData.items.map((item, index) => (
                      <TableRow key={index} className="border-b border-[#E5E5EA]">
                        <TableCell className="font-mono text-sm font-medium">{item.item_sku}</TableCell>
                        <TableCell className="font-medium">{item.item_name}</TableCell>
                        <TableCell className="text-[#0A0A0A]/60">{item.warehouse_name}</TableCell>
                        <TableCell className="text-right font-mono">{item.quantity}</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(item.avg_cost)}</TableCell>
                        <TableCell className="text-right font-mono font-medium">{formatCurrency(item.total_value)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Stock on Hand Report */}
      {activeReport === 'stock' && (
        <Card className="border border-[#E5E5EA] shadow-none">
          <CardHeader className="border-b border-[#E5E5EA] pb-4">
            <CardTitle className="text-heading text-lg">Stock on Hand</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#007AFF]"></div>
              </div>
            ) : stockData.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <Package className="w-12 h-12 text-[#0A0A0A]/20 mb-4" />
                <p className="text-[#0A0A0A]/60">No stock data available</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-[#E5E5EA]">
                    <TableHead className="text-overline text-[#0A0A0A]/50">SKU</TableHead>
                    <TableHead className="text-overline text-[#0A0A0A]/50">Item Name</TableHead>
                    <TableHead className="text-overline text-[#0A0A0A]/50">Category</TableHead>
                    <TableHead className="text-overline text-[#0A0A0A]/50">Warehouse</TableHead>
                    <TableHead className="text-overline text-[#0A0A0A]/50 text-right">On Hand</TableHead>
                    <TableHead className="text-overline text-[#0A0A0A]/50 text-right">Reserved</TableHead>
                    <TableHead className="text-overline text-[#0A0A0A]/50 text-right">Available</TableHead>
                    <TableHead className="text-overline text-[#0A0A0A]/50 text-right">Reorder Point</TableHead>
                    <TableHead className="text-overline text-[#0A0A0A]/50">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stockData.map((item, index) => (
                    <TableRow key={index} className="border-b border-[#E5E5EA]">
                      <TableCell className="font-mono text-sm font-medium">{item.item_sku}</TableCell>
                      <TableCell className="font-medium">{item.item_name}</TableCell>
                      <TableCell className="text-[#0A0A0A]/60">{item.category || '-'}</TableCell>
                      <TableCell className="text-[#0A0A0A]/60">{item.warehouse_name}</TableCell>
                      <TableCell className="text-right font-mono">{item.quantity_on_hand}</TableCell>
                      <TableCell className="text-right font-mono">{item.quantity_reserved}</TableCell>
                      <TableCell className={`text-right font-mono ${item.is_low_stock ? 'text-[#FF3B30]' : ''}`}>
                        {item.available}
                      </TableCell>
                      <TableCell className="text-right font-mono text-[#0A0A0A]/60">{item.reorder_point}</TableCell>
                      <TableCell>
                        {item.is_low_stock ? (
                          <Badge className="bg-[#FF3B30]/10 text-[#FF3B30]">Low Stock</Badge>
                        ) : (
                          <Badge className="bg-[#34C759]/10 text-[#34C759]">OK</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
